import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useTelegramWebApp } from "../../hooks/useTelegramWebApp.js";

import { orderProductsForUser } from "./order.js";
import type { ProductCard } from "./products.js";

/**
 * Stack of three product cards shown inside the post-share success
 * modal. Order is deterministic per user-week (see `orderProductsForUser`).
 */
export function CrossPromoCards() {
  const { t } = useTranslation();
  const wa = useTelegramWebApp();
  const userId = wa?.initDataUnsafe.user?.id ?? null;

  const products = useMemo(
    () => orderProductsForUser({ userId, date: new Date() }),
    [userId],
  );

  function open(p: ProductCard) {
    const url = p.url();
    if (wa?.openLink) wa.openLink(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <ul className="mt-4 flex flex-col gap-2">
      {products.map((p) => (
        <li
          key={p.id}
          className="flex items-center gap-3 rounded-lg border border-[var(--tg-secondary-bg)] bg-[var(--tg-secondary-bg)] px-3 py-2 text-left"
        >
          {p.iconUrl ? (
            <img
              aria-hidden
              src={p.iconUrl}
              alt=""
              className="h-8 w-8 shrink-0 object-contain"
            />
          ) : (
            <span aria-hidden className="text-2xl">
              {p.icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[var(--tg-text)]">
              {t(p.titleKey)}
            </div>
            <div className="text-xs text-[var(--tg-hint)]">
              {t(p.blurbKey)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              open(p);
            }}
            className="shrink-0 rounded-md bg-[var(--tg-link)] px-2.5 py-1 text-xs font-medium text-white"
          >
            {t(p.ctaKey)}
          </button>
        </li>
      ))}
    </ul>
  );
}
